import * as Sentry from '@sentry/react';
import {
  ClientType,
  generators,
  HostMessageType,
  HostSettingsPayload,
  Invitation,
  LobbyMessageType,
  PresenceMessageType,
  Profile,
  QueueSettingsBody,
  validators,
  websocket,
} from '@playcastdotio/Messaging';
import { isElectron, type DashboardFunctions } from '..';
import { disableRemoteControl } from './peers/remoteControl';
import { getIpcMessagingInterface } from '../messaging/protocols';
import * as ipc from '../messaging/handlers/ipc';
import { sendMessage as hostRealtimeSend } from './realtime';
import {
  EncoderConfig,
  EncoderStatus,
  GameClipConfigState,
  GlobalInputAccess,
  HostQrCodeParameters,
  PlaycastGamepadId,
  PlaycastHostEdition,
  PlaycastWindowState,
  PlayerQrCodeParameters,
} from '@playcastdotio/CoreTypes';
import { getStateStore } from '../stores';
import { initializeElectronEvents } from './events';
import { kickPlayer, setPresetGroup, setShouldHandlePanics, startStream, stopStream } from './peers/host';
import { getElectronStores } from '../stores/electronStores';
import { AfkPreventionState, functionConnectionState, networkMetricsState, regionOverrideState } from '../stores/shared';
import { initializeMediaManager, populateDesktopAudioSelection } from './managers/mediaManager';
import { setAssignmentsForPlayer } from './events/gamepadEvents';
import constants from '@playcastdotio/GlobalConfig';
import { PlaycastWebrtcPresetGroup } from '@playcastdotio/Peer';
import { setShouldHandlePlayjectorCrash } from './events/onPlayjectorCrash';
import { getNotifyMessages } from '@playcastdotio/StateStore';
import { ipcSend } from '../messaging/protocols/ipc';
import { USE_MEDIA_COORDINATOR, USE_PAVILION_COORDINATOR, USE_SESSION_COORDINATOR } from '../../config/featureFlags';
import { appCoordinator } from '../../coordinators/init';
import { invalidateInvitation, setInvitation } from './managers/invitation';
import { sendEmail, sendSms } from './managers/outbound';
import {
  createMatchmakerLobby,
  destroyMatchmakerLobby,
  joinMatchmakerLobby,
  leaveMatchmakerLobby,
  queryOwnerAliases,
  queryProfile,
  queryProfileInvitations,
  startMatchmakerLobby,
  updateProfile,
} from './realtime/handlers/profile';
import { pingClient, bandwidthClient } from '@playcastdotio/PingClient';
import { kickPlayerFromLobby, playjectorStartPavilion, playjectorStopPavilion, startMatchmakerStateMachine } from './managers/matchmaker';
import { sendTwitchBot, twitchLeaveChannel } from './apiV2';
import { sendDiscordBot } from './apiV2';
import { setAfkPreventionEnabled, setAfkPreventionSettings, testAfkPreventionKey } from './managers/afkPrevention';
import { runAttestationFlow } from '../services/machineIdAttestationFlow';

const QR_CODE_OVERLAY_CONFIG = {
  name: 'qr-code',
  destName: '',
  translate: [0.78, 0.8, 0.0] as [number, number, number],
  rotate: [0.0, 0.0, 0.0] as [number, number, number],
  scale: [0.18, 0.18, 0.18] as [number, number, number],
  visible: true,
} as const;

const state = getStateStore('ipc');
const electronState = getElectronStores();
const ipcMessaging = getIpcMessagingInterface();

const isNonEmptyString = (v: unknown): v is string => typeof v === 'string' && v.length > 0;

export const electronLogin = () => {
  console.log('[IPC-Fns] Host login called');
  window.open(constants.MARKETING_BASE_URL + 'host-login', '_blank');
  // ipcMessaging.send(generators.host.login(state.authState.currentState.hostAuthId));
};

export const electronLogout = () => {
  console.log('[IPC-Fns] Host logout called');
  ipcMessaging.send(generators.host.logout(state.authState.currentState.hostAuthId));
};

export const electronClipboardCopy = (text: string) => {
  console.log('[IPC-Fns] Clipboard copy called');
  if (window.playcast?.copyToClipboard) {
    window.playcast.copyToClipboard(text);
  }
};

export const electronChangeShadowcamPreview = (shadowcam: string) => {
  state.hostRuleState.updateState({
    ...state.hostRuleState.currentState,
    videoPreviewCamera: shadowcam,
  });
};

// export const electronOpenFolderDialog = async (): Promise<string> => {
//   console.log('[IPC-Fns] Opening folder dialog');
//   if (window.playcast?.openFolderDialog) {
//     return window.playcast.openFolderDialog();
//   }

//   return '';
// };

export const devFunctions = () => {
  return {
    setApiRemoteUrl: (url: string) => {
      state.apiState.updateState({
        ...state.apiState.currentState,
        remoteUrl: url,
      });
    },
    reconnectMessageCaptureClient: () => {
      if (isElectron) {
        ipcMessaging.send(generators.host.reconnectMessageCaptureClient(state.authState.currentState.hostAuthId));
      }
    },
    showWebrtcInternals: () => {
      if (isElectron) {
        ipcMessaging.send(generators.host.showWebrtcInternals(state.authState.currentState.hostAuthId));
        console.log('[IPC-Fns] Showing webrtc internals');
      }
    },
    showGpuInternals: () => {
      if (isElectron) {
        ipcMessaging.send(generators.host.showGpuInternals(state.authState.currentState.hostAuthId));
        console.log('[IPC-Fns] Showing gpu internals');
      }
    },
    launchBigPictureMode: () => {
      startStream();
      playjectorStartPavilion();
    },
    stopPlayjectorPavilion: () => {
      playjectorStopPavilion();
    },
    updateRegionOverride: (regionOverride: string | undefined) => {
      regionOverrideState.updateState({ regionOverride: regionOverride as any });
    },
    apiState: state.apiState,
  };
};

export const setWindowState = async (windowState: PlaycastWindowState) => {
  if (windowState === 'closed') {
    setShouldHandlePlayjectorCrash(false);
    ipcFunctions?.stopStream('host:host_app_closed');
    disableRemoteControl();
  }
  ipcMessaging.send(generators.host.setWindowState(state.authState.currentState.hostAuthId, windowState));
};

export const setMicrophonePreviewVolume = (volume: number) => {
  state.micPreviewState.updateState({
    level: volume,
  });
};

let ipcFunctions: DashboardFunctions;
export const getIpcFunctions = (): DashboardFunctions => {
  if (!isElectron) {
    throw new Error('IPC interface not available in browser');
  }
  if (ipcFunctions) {
    console.warn('[IPC-Fns] IPC interface already exists, returning original instance.');
    return ipcFunctions;
  }
  functionConnectionState.updateState({
    connected: true,
    initializing: false,
    functionKey: '',
    error: false,
    remoteClientId: '',
  });

  initializeElectronEvents();

  populateDesktopAudioSelection();

  // Pavilion capture is owned by MediaCoordinator (same path as beacon). The legacy
  // media manager opens a second getUserMedia graph per ShadowCam, which competes
  // with the game encoder. The login shell also skips it so idle detection can sleep.
  // Prime still uses the legacy graph. Beacon keeps it: that path is the reference.
  const isPavilionLoginShell = typeof window !== 'undefined' && window.playcast?.pavilionLoginShell === true;
  const pavilionCoordinatorOwnsCapture = USE_MEDIA_COORDINATOR && USE_PAVILION_COORDINATOR;
  if (!isPavilionLoginShell && !pavilionCoordinatorOwnsCapture) {
    initializeMediaManager();
  }

  startMatchmakerStateMachine();

  // pingClient.initialize(10 * 1000, (results) => {
  //   console.log('[IPC-Fns] Ping results', results);
  //   networkMetricsState.updateState({
  //     ...networkMetricsState.currentState,
  //     ping: results,
  //   });
  // });
  // pingClient.start();

  // bandwidthClient.initialize({
  //   interval: 60 * 1000,
  //   downloadSize: 10 * 1024 * 1024, // 10MB
  //   uploadSize: 10 * 1024 * 1024, // 10MB
  //   callback: (results) => {
  //     console.log('[IPC-Fns] Bandwidth results', results);
  //     networkMetricsState.updateState({
  //       ...networkMetricsState.currentState,
  //       bandwidth: results,
  //     });
  //   },
  // });
  // bandwidthClient.start();

  const initialHostMessages = getNotifyMessages(['hostBackend']);
  initialHostMessages['hostBackend'].forEach((message) => {
    ipcSend(message);
  });

  appCoordinator.scheduleResyncCheck();

  ipcMessaging.receive((message) => {
    ipc.handleIncomingMessages(message);
  });

  ipcFunctions = {
    startStream: async () => {
      console.log('[IPC-Fns] Starting stream');

      // Make sure the qr code is reset when starting a new stream
      state.hostState.updateState({
        ...state.hostState.currentState,
        streamQrImageBase64: '',
      });

      // if qr code is enabled, create overlay
      if (state.hostState.currentState.showStreamQrCode) {
        try {
          ipcFunctions.createQrCodeOverlay();
        } catch (error) {
          console.error('Failed to create QR code overlay:', error);
        }
      }

      setInvitation({ invalid: false });
      await startStream();
    },
    stopStream: async (reason?: string) => {
      if (state.hostState.currentState.status === 'starting' || state.hostState.currentState.status === 'streaming') {
        // kick all players via realtime
        const peerIds = Object.keys(state.playerState.currentState.players);
        peerIds.forEach((peerId) => {
          const player = structuredClone(state.playerState.currentState.players[peerId]);
          if (!player) {
            console.error('[IPC-Fns] Player not found');
            return;
          }
          hostRealtimeSend(
            websocket.generators.lobby(LobbyMessageType.LOBBY_KICK, {
              invitationId: state.invitationStateV2.currentState.id,
              playerId: player.invitationPlayerId,
              reason: 'Stream ended',
            }),
          );
        });

        // Kick all pending players
        const pendingPlayers = state.lobbyStateV2.currentState.knocks.pending;
        const acceptedPlayers = state.lobbyStateV2.currentState.knocks.accepted;
        const allPlayers = [...pendingPlayers, ...acceptedPlayers];
        allPlayers.forEach((player) => {
          hostRealtimeSend(
            websocket.generators.lobby(LobbyMessageType.LOBBY_KICK, {
              invitationId: state.invitationStateV2.currentState.id,
              playerId: player.playerId,
              reason: 'Stream ended',
            }),
          );
        });

        state.lobbyStateV2.reset();

        // invalidate the invitation
        await invalidateInvitation('Host stopped stream');

        // STRANGLER-FIG: Skip PRESENCE_UPDATE when SessionCoordinator is active
        if (!USE_SESSION_COORDINATOR) {
          hostRealtimeSend(
            websocket.generators.presence(PresenceMessageType.PRESENCE_UPDATE, {
              clientType: ClientType.HOST,
              presence: {
                ...state.hostPresenceState.currentState,
                status: 'idle',
                runningGame: undefined,
                playerCount: 0,
              },
            }),
          );
        }

        state.qrCodeBase64State.updateState('');

        // delete the qr code overlay
        ipcMessaging.send(generators.overlay.overlayDeleteOverlay(state.authState.currentState.hostAuthId, 'qr-code'));

        stopStream(reason ?? 'host:ipc_stopstream');
      }
    },
    setStreamTitle: (title: string) => {
      state.invitationStateV2.updateState({
        ...state.invitationStateV2.currentState,
        description: title,
      });
    },
    showVideoPreview: (show?: boolean) => {
      if (show === undefined) {
        show = state.hostRuleState.currentState.showVideoPreview;
      }
      state.hostRuleState.updateState({
        ...state.hostRuleState.currentState,
        showVideoPreview: !show,
      });
    },
    changeDisplay: (displayId: number) => {
      console.warn('CHANGE DISPLAY', displayId);
      if (displayId === 999) {
        ipcFunctions?.setDisplaySurfing(true);
        ipcFunctions?.setDesktopAudio('desktop');
      } else {
        ipcFunctions?.setDisplaySurfing(false);
        // Set audio source back to Desktop Audio
        ipcFunctions?.setDesktopAudio('desktop');
        state.displayState.updateState({
          ...state.displayState.currentState,
          info: {
            selectedDisplayId: displayId,
          },
        });
        ipcMessaging.send(generators.camera.setPicking(true));
        const sourceProperties = state.displayState.currentState.sourceProperties.find((source) => source.sourceId === displayId);
        if (!sourceProperties) {
          console.error('[IPC-Fns] Source properties not found');
          return;
        }
        const activeCams = state.activeShadowcamState.currentState.active;
        activeCams.forEach((cam) => {
          ipcMessaging.send(
            generators.camera.setSource(cam, {
              sourceId: sourceProperties.sourceId,
              adapterId: sourceProperties.adapterId,
              desktopCoordinates: sourceProperties.desktopCoordinates,
              virtualCoordinates: sourceProperties.virtualCoordinates,
            }),
          );
        });
      }
    },
    setOutboundVideoDevice: (deviceId: string, windowCapturePid?: number) => {
      console.log('[IPC-Fns] Setting outbound video device', deviceId);
      if (deviceId === '0') {
        state.showStaticImageState.updateState({
          showStaticImage: true,
        });
        // Also re-replicate the staticImageState.
        const players = state.playerState.currentState.players;
        Object.keys(players).forEach((playerId) => {
          state.staticImageState.updateState({
            ...state.staticImageState.currentState,
            players: {
              ...state.staticImageState.currentState.players,
              [playerId]: { staticImageBase64: state.staticImageState.currentState.staticImageBase64 },
            },
          });
        });
      } else {
        state.showStaticImageState.updateState({
          showStaticImage: false,
        });
      }
      if (deviceId === 'display' || deviceId === 'followMouse') {
        // Set audio source back to desktop
        ipcFunctions?.setDesktopAudio('desktop');
      }
      if (deviceId === 'gameCapture') {
        ipcFunctions?.setDesktopAudio('gameCapture');
        deviceId = 'display';
      }
      state.videoState.updateState({
        ...state.videoState.currentState,
        enabled: deviceId !== '0',
        selectedDevice: deviceId,
        windowCapturePid: windowCapturePid,
      });
    },
    setMicrophone: (deviceId: string) => {
      state.audioState.updateState({
        ...state.audioState.currentState,
        microphoneSources: state.audioState.currentState.microphoneSources.map((microphone) => {
          return {
            ...microphone,
            selected: microphone.deviceId === deviceId,
          };
        }),
      });
      console.debug('[IPC-Fns] Selected microphone', deviceId);
    },
    setMicrophoneMuted: (enabled: boolean) => {
      state.audioState.updateState({
        ...state.audioState.currentState,
        microphoneMuted: enabled,
      });
    },
    setMicrophoneVolume: (volume: number) => {
      state.audioState.updateState({
        ...state.audioState.currentState,
        microphoneVolume: volume,
      });
    },
    setDesktopAudio: (deviceId: string) => {
      console.debug('[IPC-Fns] Selected desktop audio', deviceId);

      // Check if the deviceId exists in the current desktop audio sources
      const deviceExists = state.audioState.currentState.desktopAudioSources.some((source) => source.deviceId === deviceId);

      if (!deviceExists) {
        console.warn('[IPC-Fns] Desktop audio device not found in available sources:', deviceId);
        return;
      }

      state.audioState.updateState({
        ...state.audioState.currentState,
        desktopAudioSources: state.audioState.currentState.desktopAudioSources.map((source) => {
          return {
            ...source,
            selected: source.deviceId === deviceId,
          };
        }),
      });
    },
    setDesktopAudioMuted: (enabled: boolean) => {
      state.audioState.updateState({
        ...state.audioState.currentState,
        desktopAudioMuted: enabled,
      });
    },
    setDesktopAudioVolume: (volume: number) => {
      state.audioState.updateState({
        ...state.audioState.currentState,
        desktopAudioVolume: volume,
      });
    },
    kickPlayer: (playerId: string) => {
      kickPlayer(playerId);
    },
    kickAll: () => {
      const playerIds = Object.keys(state.playerState.currentState.players);
      console.log('[IPC-Fns] Kicking all players', playerIds);
      playerIds.forEach((playerId) => {
        kickPlayer(playerId);
      });
    },
    setAllowedGamepadInput: (allowed: boolean, playerId?: string) => {
      if (playerId) {
        console.log('[IPC-Fns] Setting gamepad access', allowed, playerId);
        const current = state.playerState.currentState.players[playerId]?.access;
        if (!current) {
          console.error('[IPC-Fns] (Gamepad) Player not found');
        }
        const newState = {
          ...current,
          gamepad: allowed,
        };
        updateInputAccess(newState, playerId);
      } else {
        const current = state.globalInputState.currentState;
        updateInputAccess({
          ...current,
          gamepad: allowed,
        });
      }
    },
    setAllowedKeyboardInput: (allowed: boolean, playerId?: string) => {
      if (playerId) {
        console.log('[IPC-Fns] Setting keyboard access', allowed, playerId);
        const current = state.playerState.currentState.players[playerId]?.access;
        if (!current) {
          console.error('[IPC-Fns] (Keyboard) Player not found');
        }
        const newState = {
          ...current,
          keyboard: allowed,
        };
        updateInputAccess(newState, playerId);
      } else {
        const current = state.globalInputState.currentState;
        console.log('[IPC-Fns] Setting keyboard access', allowed);
        updateInputAccess({
          ...current,
          keyboard: allowed,
        });
      }
    },
    setAllowedMouseInput: (allowed: boolean, playerId?: string) => {
      if (playerId) {
        console.log('[IPC-Fns] Setting mouse access', allowed, playerId);
        const current = state.playerState.currentState.players[playerId]?.access;
        if (!current) {
          console.error('[IPC-Fns] (Mouse) Player not found');
        }
        const newState = {
          ...current,
          mouse: allowed,
        };
        updateInputAccess(newState, playerId);
      } else {
        const current = state.globalInputState.currentState;
        updateInputAccess({
          ...current,
          mouse: allowed,
        });
      }
    },
    setAllowedTouchInput: (allowed: boolean, playerId?: string) => {
      if (playerId) {
        console.log('[IPC-Fns] Setting touch access', allowed, playerId);
        const current = state.playerState.currentState.players[playerId]?.access;
        if (!current) {
          console.error('[IPC-Fns] (Touch) Player not found');
        }
        const newState = {
          ...current,
          touch: allowed,
        };
        updateInputAccess(newState, playerId);
      } else {
        const current = state.globalInputState.currentState;
        updateInputAccess({
          ...current,
          touch: allowed,
        });
      }
    },
    setDisplaySurfing: (enabled: boolean) => {
      console.debug('[IPC-Fns] Enabling surfing', enabled);
      ipcMessaging.send(generators.camera.setPicking(enabled, state.authState.currentState.hostAuthId));
      const activeCams = state.activeShadowcamState.currentState.active;
      activeCams.forEach((cam) => {
        ipcMessaging.send(generators.camera.autoPick(cam, enabled, state.authState.currentState.hostAuthId));
      });
    },
    startUpdate: () => {
      console.log('[IPC-Fns] Starting update');
      ipcMessaging.send(generators.host.startUpdate(state.authState.currentState.hostAuthId));
    },
    ignoreUpdate: () => {
      console.log('[IPC-Fns] Ignoring update');
      electronState.versionState.updateState({
        ...electronState.versionState.currentState,
        autoUpdate: false,
        hasUpdate: false,
      });
      const currentIgnoredUpdatesString = localStorage.getItem('ignoredUpdates') || '';
      const currentIgnoredUpdates = currentIgnoredUpdatesString ? JSON.parse(currentIgnoredUpdatesString) : [];
      currentIgnoredUpdates.push(electronState.versionState.currentState.updateVersion);
      localStorage.setItem('ignoredUpdates', JSON.stringify(currentIgnoredUpdates));
    },
    // showVideo: (show: boolean, playerId?: string) => {
    //   if (playerId) {
    //     const player = state.playerState.currentState.players[playerId];
    //     if (player) {
    //       state.playerState.updateState({
    //         players: {
    //           ...state.playerState.currentState.players,
    //           [playerId]: {
    //             ...player,
    //             showVideo: show,
    //           },
    //         },
    //       });
    //     }
    //   } else {
    //     state.videoState.updateState({
    //       ...state.videoState.currentState,
    //       enabled: show,
    //     });
    //   }
    // },
    setStaticImage: (imageBase64Blob: string, playerId?: string) => {
      const imageState = state.staticImageState.currentState;
      if (playerId) {
        imageState.players[playerId] = { staticImageBase64: imageBase64Blob };
      } else {
        imageState.staticImageBase64 = imageBase64Blob;
        Object.keys(imageState.players).forEach((playerId) => {
          imageState.players[playerId] = { staticImageBase64: imageBase64Blob };
        });
      }
      state.staticImageState.updateState({ ...imageState });
    },
    setQrCodeParameters: (qrCodeParameters: PlayerQrCodeParameters, playerId?: string) => {
      if (playerId) {
        let player = state.qrCodeState.currentState.players[playerId];
        if (!player) {
          player = {
            ...state.qrCodeState.currentState.defaultPlayerState,
          };
        }
        state.qrCodeState.updateState({
          ...state.qrCodeState.currentState,
          players: {
            ...state.qrCodeState.currentState.players,
            [playerId]: {
              ...player,
              ...qrCodeParameters,
            },
          },
        });
      } else {
        const newState = state.qrCodeState.currentState;
        newState.defaultPlayerState = qrCodeParameters;
        Object.keys(newState.players).forEach((playerId) => {
          newState.players[playerId] = {
            ...newState.players[playerId],
            ...qrCodeParameters,
          };
        });
        state.qrCodeState.updateState(newState);
      }
    },
    setHostQrCodeParameters: (hostQrCodeParameters: HostQrCodeParameters) => {
      state.hostQrCodeState.updateState({
        ...hostQrCodeParameters,
      });
    },
    fetchAudioSources: () => {
      ipcMessaging.send(generators.host.fetchAudioSources(state.authState.currentState.hostAuthId));
    },
    setDesktopAudioSource: (sourceName: string, playerId?: string) => {
      console.log('[IPC-Fns] Setting desktop audio source', sourceName, playerId);
      // if (playerId) {
      //   let player = state.audioState.currentState.players[playerId];
      //   if (!player) {
      //     player = {
      //       desktopAudioMuted: state.audioState.currentState.desktopAudioMuted,
      //       microphoneMuted: state.audioState.currentState.microphoneMuted,
      //       desktopAudioSource: state.audioState.currentState.desktopAudioSource,
      //     };
      //   }
      //   state.audioState.updateState({
      //     ...state.audioState.currentState,
      //     [playerId]: {
      //       ...player,
      //       desktopAudioSource: sourceName,
      //     },
      //   });
      // } else {
      //   state.audioState.updateState({
      //     ...state.audioState.currentState,
      //     desktopAudioSource: sourceName,
      //   });
      // }
    },
    setGamepadSharing: (gamepadIndex: number, enabled: boolean) => {
      if (gamepadIndex === 0) {
        state.gamepadState.updateState({
          ...state.gamepadState.currentState,
          shareGamepadOne: enabled,
        });
      }
      if (gamepadIndex === 1) {
        state.gamepadState.updateState({
          ...state.gamepadState.currentState,
          shareGamepadTwo: enabled,
        });
      }
      if (gamepadIndex === 2) {
        state.gamepadState.updateState({
          ...state.gamepadState.currentState,
          shareGamepadThree: enabled,
        });
      }
      if (gamepadIndex === 3) {
        state.gamepadState.updateState({
          ...state.gamepadState.currentState,
          shareGamepadFour: enabled,
        });
      }
    },
    setAssignGamepadsAutomatically: (enabled: boolean) => {
      state.gamepadState.updateState({
        ...state.gamepadState.currentState,
        autoAssign: enabled,
      });
    },
    pauseInput: (paused: boolean, playerId?: string) => {
      if (playerId) {
        const player = structuredClone(state.playerState.currentState.players[playerId]);
        if (player) {
          state.pauseInputState.updateState({
            ...state.pauseInputState.currentState,
            players: {
              ...state.pauseInputState.currentState.players,
              [playerId]: {
                ...player,
                paused,
              },
            },
          });
        }
      } else {
        state.pauseInputState.updateState({
          ...state.pauseInputState.currentState,
          paused,
        });
        ipcMessaging.send(generators.input.setPaused(state.authState.currentState.hostAuthId, paused));
      }
    },
    acceptKnock: (knockId: string) => {
      const lobby = state.lobbyStateV2.currentState;
      // remove the player from the pending list
      const player = lobby.knocks.pending.find((player) => player.playerId === knockId);
      if (!player) {
        console.error('[IPC-Fns] Player not found in pending list', knockId);
        return;
      }
      lobby.knocks.pending = lobby.knocks.pending.filter((player) => player.playerId !== knockId);
      lobby.knocks.accepted.push(player);

      hostRealtimeSend(
        websocket.generators.lobby(LobbyMessageType.LOBBY_ACCEPT, {
          invitationId: state.invitationStateV2.currentState.id,
          playerId: knockId,
        }),
      );
      return;
    },
    rejectKnock: (knockId: string) => {
      const invitationId = state.invitationStateV2.currentState.id;
      if (!invitationId) {
        console.error('[IPC-Fns] Invitation ID not found');
        return;
      }
      const isAccepted = state.lobbyStateV2.currentState.knocks.accepted.find((player) => player.playerId === knockId);
      const isPending = state.lobbyStateV2.currentState.knocks.pending.find((player) => player.playerId === knockId);

      if (isAccepted) {
        state.lobbyStateV2.updateState({
          ...state.lobbyStateV2.currentState,
          knocks: {
            ...state.lobbyStateV2.currentState.knocks,
            accepted: state.lobbyStateV2.currentState.knocks.accepted.filter((player) => player.playerId !== knockId),
          },
        });
      } else if (isPending) {
        state.lobbyStateV2.updateState({
          ...state.lobbyStateV2.currentState,
          knocks: {
            ...state.lobbyStateV2.currentState.knocks,
            pending: state.lobbyStateV2.currentState.knocks.pending.filter((player) => player.playerId !== knockId),
          },
        });
      }

      hostRealtimeSend(websocket.generators.lobby(LobbyMessageType.LOBBY_KICK, { invitationId, playerId: knockId }));
      return;
    },
    reorderLobby: (pendingPlayers: string[]) => {
      hostRealtimeSend(
        websocket.generators.lobby(LobbyMessageType.LOBBY_REORDER, {
          invitationId: state.invitationStateV2.currentState.id,
          playerOrder: pendingPlayers,
        }),
      );
      // reorder the state in pending
      const currentPending = state.lobbyStateV2.currentState.knocks.pending;

      const newPending = pendingPlayers
        .map((playerId) => {
          return currentPending.find((player) => player.playerId === playerId);
        })
        .filter((player) => player !== undefined);

      // update the state with the new order
      state.lobbyStateV2.updateState({
        ...state.lobbyStateV2.currentState,
        knocks: {
          ...state.lobbyStateV2.currentState.knocks,
          pending: newPending,
        },
      });
      return;
    },
    updateQueueSettings: (enabled: boolean, settings: QueueSettingsBody, instantConnect: boolean) => {
      if (state.hostRuleState.currentState.hostHandlesQueueAccepting) {
        state.hostRuleState.updateState({
          ...state.hostRuleState.currentState,
          apiAutoAccept: false,
          hostAutoAccept: settings.acceptAll,
        });
        settings.acceptAll = false;
      }
      const queueState = state.queueState.currentState;
      const queueId = queueState.id;
      queueState.enabled = enabled;
      queueState.settings = {
        ...queueState.settings,
        ...settings,
      };
      // state.queueState.updateState(queueState);
      // api.updateQueueSettings(queueId, enabled, settings);

      // const currentInstantConnect = state.invitationStateV2.currentState.manifest.instantConnect;
      // if (currentInstantConnect !== instantConnect) {
      //   state.invitationStateV2.updateState({
      //     ...state.invitationStateV2.currentState,
      //     instantConnect,
      //   });
      //   console.log('[IPC-Fns] Updating instant connect', instantConnect);
      //   // api.updateInvitation();
      // }
    },
    updateLobbySettings: (update: Invitation) => {
      const currentInvitationState = state.invitationStateV2.currentState;
      if (!currentInvitationState) {
        console.error('[IPC-Fns] Invitation not found');
        return;
      }
      setInvitation(update);
    },
    updateHostSettings: (hostSettings: HostSettingsPayload) => {
      console.log('[IPC-Fns] Updating host settings', hostSettings);
      state.hostSettingsState.updateState({
        ...state.hostSettingsState.currentState,
        ...hostSettings,
      });
      hostRealtimeSend(
        websocket.generators.host(HostMessageType.HOST_SETTINGS, {
          ...state.hostSettingsState.currentState,
        }),
      );
    },
    updateInvitationDescription: (description: string) => {
      console.log('[IPC-Fns] Updating invitation description', description);
      console.log(state.invitationStateV2.currentState);
      setInvitation({ description });
      return;
    },
    setGamepadAssignmentEnabled: (playerId: string, gamepadIndex: number, enabled: boolean) => {
      // Allow position changes regardless of current input state
      // This enables changing gamepad positions even when users are sending inputs
      const maps = state.gamepadState.currentState.playerMappings;
      const indexes = maps[playerId]?.gamepadIndexes || [];
      let newAssignments: number[] = [];
      if (enabled) {
        // if the gamepadIndex is not already in the list, add it.
        if (!indexes.includes(gamepadIndex as PlaycastGamepadId)) {
          newAssignments = [...indexes, gamepadIndex];
        } else {
          newAssignments = indexes;
        }
        // Ensure gamepad access is enabled when assigning a position
        if (!state.playerState.currentState.players[playerId]?.access.gamepad) {
          const current = state.playerState.currentState.players[playerId]?.access;
          if (current) {
            const newState = {
              ...current,
              gamepad: true,
            };
            updateInputAccess(newState, playerId);
          }
        }
      } else {
        newAssignments = indexes.filter((index) => index !== gamepadIndex);
      }
      // order the new assignments
      newAssignments = newAssignments.sort();
      console.log('[IPC-Fns] Setting gamepad assignments', playerId, newAssignments);
      setAssignmentsForPlayer(playerId, newAssignments);
    },
    sendInvitationEmail: (email: string, message: string, invitationId?: string) => {
      const inviteId = invitationId || state.invitationStateV2.currentState.id;
      sendEmail(inviteId, email, message);
    },
    sendInvitationSms: (phone: string, message: string, invitationId?: string) => {
      const inviteId = invitationId || state.invitationStateV2.currentState.id;
      sendSms(inviteId, phone, message);
    },
    renameHost: (newName: string, remoteId: string) => {
      // get info from remoteHostList about remoteId.
      type RemoteData =
        | {
            machineId: string;
            environment: string;
            version: string;
            uniqueId: string;
          }
        | undefined;
      const allActiveRemotes = Object.values(state.remoteHostList.currentState.hosts);
      const allInactiveRemotes = Object.values(state.remoteHostList.currentState.inactive);
      let remote: RemoteData = allActiveRemotes.find((host) => host.remoteId === remoteId);
      if (!remote) {
        remote = allInactiveRemotes.find((host) => host.remoteId === remoteId);
      }
      if (!remote) {
        console.error('[IPC-Fns] Remote not found');
        return;
      }

      // we just update the realtime API if we're using experimental
      hostRealtimeSend(
        websocket.generators.host(HostMessageType.HOST_SETTINGS, {
          ...state.hostSettingsState.currentState,
        }),
      );
      return;
    },
    setFilterGamepadButtons: (filtered: boolean) => {
      state.hostRuleState.updateState({
        ...state.hostRuleState.currentState,
        enableGamepadFiltering: filtered,
      });
    },
    setMaxGamepadAssignments: (maxAssignments: number) => {
      maxAssignments = Math.min(4, maxAssignments);
      maxAssignments = Math.max(0, maxAssignments);
      state.gamepadState.updateState({
        ...state.gamepadState.currentState,
        maxAssignments,
      });
    },
    setWebrtcPresetGroup: (presetGroup: PlaycastWebrtcPresetGroup) => {
      console.log('[IPC-Fns] Setting webrtc preset group', presetGroup);
      setPresetGroup(presetGroup);
    },
    setWebrtcShouldHandlePanic: (shouldHandlePanic: boolean) => {
      setShouldHandlePanics(shouldHandlePanic);
    },
    processLaunch: (launchId: string, args: string) => {
      console.log('[IPC-Fns] Launching process', launchId, args);
      // Legacy interface - constructs a Steam launch package for backward compatibility
      ipcMessaging.send(generators.process.processLaunch(
        state.authState.currentState.hostAuthId,
        'standard',
        {
          launcher: 'steam',
          readyWaitTimeMs: 30000,
          launchId: launchId,
          arguments: args,
          filters: {},
        }
      ));
    },
    processRestart: (argument: string) => {
      console.log('[IPC-Fns] Restarting process');
      ipcMessaging.send(generators.process.processRestart(state.authState.currentState.hostAuthId, argument));
    },
    processTerminate: (type: 'default' | 'pavilion') => {
      console.log('[IPC-Fns] Terminating process', type);
      ipcMessaging.send(generators.process.processTerminate(state.authState.currentState.hostAuthId, type));
    },
    processSetPolicy: (
      nonFocusInput: boolean,
      constrainMouse: boolean,
      allowToolbarAccess: boolean,
      forceProcessOnTop: boolean,
      launchTimeout: number,
      captureAudio: boolean,
      captureVideo: boolean,
      saveEnumeratedApps: boolean,
      onlyExitOnDisconnect: boolean,
      enableHostDominanceKick?: boolean,
    ) => {
      console.log('[IPC-Fns] Setting process policy');

      // If window capture is enabled (constrainMouse is true), try to set audio to Game Capture
      if (constrainMouse) {
        const gameCaptureSource = state.audioState.currentState.desktopAudioSources.find((source) => source.deviceId === 'gameCapture');
        if (gameCaptureSource) {
          ipcFunctions?.setDesktopAudio('gameCapture');
        }
      } else {
        // If window capture is disabled, set back to Desktop Audio
        ipcFunctions?.setDesktopAudio('desktop');
      }

      ipcMessaging.send(
        generators.process.processSetPolicy(
          state.authState.currentState.hostAuthId,
          nonFocusInput,
          constrainMouse,
          allowToolbarAccess,
          forceProcessOnTop,
          launchTimeout,
          captureAudio,
          captureVideo,
          saveEnumeratedApps,
          onlyExitOnDisconnect,
          enableHostDominanceKick,
        ),
      );
    },
    processGetPolicy: () => {
      console.log('[IPC-Fns] Getting process policy');
      ipcMessaging.send(generators.process.processGetPolicy(state.authState.currentState.hostAuthId));
    },
    processEnumRunning: () => {
      console.log('[IPC-Fns] Enumerating running processes');
      ipcMessaging.send(generators.process.processEnumRunning(state.authState.currentState.hostAuthId));
    },
    processSelect: (processId: number) => {
      console.log('[IPC-Fns] Selecting process', processId);
      ipcMessaging.send(generators.process.processSelect(state.authState.currentState.hostAuthId, processId));
      ipcFunctions?.setDisplaySurfing(false);
    },
    processDisconnect: () => {
      console.log('[IPC-Fns] Disconnecting process');
      ipcMessaging.send(generators.process.processDisconnect(state.authState.currentState.hostAuthId));
    },
    systemLogOff: (username: string) => {
      console.log('[IPC-Fns] Logging off active user');
      ipcMessaging.send(generators.system.logOffSession(username, state.authState.currentState.hostAuthId));
    },
    overlayCreateImageOverlay: (
      overlayName: string,
      destName: string,
      imagePath: string,
      translate: number[],
      rotate: number[],
      scale: number[],
      visible: boolean,
    ) => {
      console.log('[IPC-Fns] Creating image overlay');
      ipcMessaging.send(
        generators.overlay.overlayCreateImageOverlay(
          state.authState.currentState.hostAuthId,
          overlayName,
          destName,
          imagePath,
          translate,
          rotate,
          scale,
          visible,
        ),
      );
    },
    createQrCodeOverlay: async () => {
      console.log('[IPC-Fns] Creating QR code overlay');
      const tempDir = await window.playcast?.getTempDir();
      if (!tempDir) {
        console.error('Failed to get temp directory');
        return;
      }
      const qrCodePath = `${tempDir}\\playcast_qr_codes\\qr_code.png`;

      // Wait for QR code generation to complete
      try {
        let attempts = 0;
        const MAX_ATTEMPTS = 10;
        const INTERVAL = 5000; // 5 seconds

        const checkGeneration = () => {
          console.log('[IPC-Fns] Checking QR code generation', state.qrCodeGenerationState.currentState);
          const generationState = state.qrCodeGenerationState.currentState;

          attempts++;
          if (attempts >= MAX_ATTEMPTS) {
            if (generationState.error) {
              console.error('QR code generation failed after 5 attempts');
              return;
            } else if (!generationState.isGenerating) {
              console.error('QR code generation not started after 5 attempts');
              return;
            } else {
              console.error('QR code generation timed out after 5 attempts');
              return;
            }
          }

          if (generationState.isComplete) {
            try {
              ipcFunctions.overlayCreateImageOverlay(
                QR_CODE_OVERLAY_CONFIG.name,
                QR_CODE_OVERLAY_CONFIG.destName,
                qrCodePath,
                QR_CODE_OVERLAY_CONFIG.translate,
                QR_CODE_OVERLAY_CONFIG.rotate,
                QR_CODE_OVERLAY_CONFIG.scale,
                QR_CODE_OVERLAY_CONFIG.visible,
              );
            } catch (error) {
              console.error('Failed to create QR code overlay:', error);
            }
            return;
          }

          // Check again after interval
          setTimeout(checkGeneration, INTERVAL);
        };

        // Start checking
        checkGeneration();
      } catch (error) {
        console.error('Error in QR code generation process:', error);
      }
    },
    overlayDeleteOverlay: (overlayName: string) => {
      console.log('[IPC-Fns] Deleting overlay');
      ipcMessaging.send(generators.overlay.overlayDeleteOverlay(state.authState.currentState.hostAuthId, overlayName));
    },
    updateTwitchSettings: (settings) => {
      state.twitchSettingsState.updateState(settings);
      console.log('[IPC-Fns] Updating twitch settings', settings);
    },
    updateDiscordSettings: (settings) => {
      state.discordSettingsState.updateState(settings);
      console.log('[IPC-Fns] Updating discord settings', settings);
    },
    setAllowPeerConnections: (allowPeerConnections: boolean) => {
      console.warn('[IPC-Fns] Setting allowPeerConnections', allowPeerConnections);
      state.iceServerState.updateState({
        ...state.iceServerState.currentState,
        allowPeerConnections,
      });
    },
    updateQrCode: (base64QRCode: string) => {
      state.qrCodeBase64State.updateState(base64QRCode);
      console.log('[IPC-Fns] Updating QR code', base64QRCode);
    },
    updateShowStreamQrCode: (showStreamQrCode: boolean) => {
      state.hostState.updateState({
        ...state.hostState.currentState,
        showStreamQrCode: showStreamQrCode,
      });
    },
    setEncoderConfig: (config: EncoderConfig) => {
      console.log('[IPC-Fns] Setting encoder config', config);
      ipcMessaging.send(generators.encoder.setEncoderConfig(state.authState.currentState.hostAuthId, config));
    },
    updateGameClipConfig: (config: GameClipConfigState) => {
      state.gameClipConfigState.updateState({
        enabled: config.enabled,
        recordHotkey: config.recordHotkey,
        bufferDurationSeconds: config.bufferDurationSeconds,
        cameraName: config.cameraName,
        directoryPath: config.directoryPath,
      });
      console.log('[IPC-Fns] Updating game clip config', config);
    },
    updateGameClipStatus: (status: EncoderStatus) => {
      state.gameClipStatusState.updateState(status);
      console.log('[IPC-Fns] Updating game clip status', status);
    },
    setAutomaticUpdatesEnabled: (enabled: boolean) => {
      console.log('[IPC-Fns] Setting automatic updates enabled', enabled);
      ipcMessaging.send(generators.system.setAutomaticUpdatesEnabled(state.authState.currentState.hostAuthId, enabled));
    },
    getAutomaticUpdatesEnabled: () => {
      console.log('[IPC-Fns] Getting automatic updates enabled');
      ipcMessaging.send(generators.system.getAutomaticUpdatesEnabled(state.authState.currentState.hostAuthId));
    },
    changeEdition: (edition: PlaycastHostEdition) => {
      console.log('[IPC-Fns] Changing edition', edition);
      if (state.steamInstallState.currentState.steamInstall && edition === 'standard') {
        console.error('cant downgrade pavilion to standard if installed via steam');
        return;
      }
      state.hostState.updateState({
        ...state.hostState.currentState,
        edition,
      });
    },
    queryProfile: () => {
      console.log('[IPC-Fns] Querying profile');
      queryProfile();
    },
    queryOwnerAliases: () => {
      console.log('[IPC-Fns] Querying owner aliases');
      queryOwnerAliases();
    },
    queryProfileInvitations: (cursor?: string, limit?: number, searchTerm?: string) => {
      console.log('[IPC-Fns] Querying profile invitations', cursor, limit, searchTerm);
      queryProfileInvitations(cursor, limit, searchTerm);
    },
    updateProfile: (profile: Profile) => {
      console.log('[IPC-Fns] Updating profile', profile);
      updateProfile(profile);
    },
    profileCreateMatchmakerLobby: () => {
      console.log('[IPC-Fns] Creating matchmaker lobby');
      createMatchmakerLobby();
    },
    profileJoinMatchmakerLobby: () => {
      console.log('[IPC-Fns] Joining matchmaker lobby');
      joinMatchmakerLobby();
    },
    profileLeaveMatchmakerLobby: () => {
      console.log('[IPC-Fns] Leaving matchmaker lobby');
      leaveMatchmakerLobby();
    },
    profileDestroyMatchmakerLobby: (selectedInvitationId?: string) => {
      console.log('[IPC-Fns] Destroying matchmaker lobby', selectedInvitationId);
      destroyMatchmakerLobby(selectedInvitationId);
    },
    profileStartMatchmakerLobby: (useSteamGuestLogin?: boolean, regionOverride?: string) => {
      console.log('[IPC-Fns] Starting matchmaker lobby', useSteamGuestLogin, regionOverride);
      startMatchmakerLobby(useSteamGuestLogin, regionOverride);
    },
    profileSelectInvitation: (invitationId?: string) => {
      console.log('[IPC-Fns] Selecting invitation', invitationId);
      state.profileState.updateState({
        ...state.profileState.currentState,
        selectedInvitationId: invitationId,
      });
    },
    matchmakerKickPlayer: (playerId: string) => {
      console.log('[IPC-Fns] Kicking player', playerId);
      kickPlayerFromLobby(playerId);
    },
    sendTwitchBot: (invitationId: string) => {
      console.log('[IPC-Fns] Sending Twitch bot', invitationId);
      sendTwitchBot(invitationId);
    },
    sendDiscordBot: (invitationId: string) => {
      console.log('[IPC-Fns] Sending Discord bot', invitationId);
      sendDiscordBot(invitationId);
    },
    setWindowState: (windowState: PlaycastWindowState) => {
      state.windowState.updateState(windowState);
    },
    toggleEncoder: () => {
      console.log('[IPC-Fns] Toggling encoder');
      ipcMessaging.send(generators.encoder.toggleEncoder(state.authState.currentState.hostAuthId));
    },
    setAfkPreventionEnabled: (enabled: boolean) => {
      console.log('[IPC-Fns] Setting AFK prevention enabled', enabled);
      state.afkPreventionState.updateState({
        ...state.afkPreventionState.currentState,
        enabled,
      });
      setAfkPreventionEnabled(enabled);
    },
    setAfkPreventionSettings: (settings: AfkPreventionState) => {
      console.log('[IPC-Fns] Setting AFK prevention settings', settings);
      state.afkPreventionState.updateState(settings);
      setAfkPreventionSettings(settings);
    },
    testAfkPreventionKey: (keyCode: number, keyDisplayName: string) => {
      console.log('[IPC-Fns] Testing AFK prevention key', keyCode, keyDisplayName);
      testAfkPreventionKey(keyCode, keyDisplayName);
    },
    // DisplayIO - ShadowDisplay Virtual Display Driver functions
    shadowDisplayAddDisplay: (width: number, height: number, refreshRate: number, deviceName: string, serialNumber: string, guid?: string) => {
      // IMPORTANT: Do NOT auto-generate GUIDs on the host side.
      // PlayJector expects ShadowDisplay GUID strings in "{...}" format. A random UUID string will fail parsing.
      // If guid is omitted, PlayJector will create a valid GUID and return it in the reply.
      console.log('[IPC-Fns] Adding virtual display', { width, height, refreshRate, deviceName, serialNumber, guid });

      const msg = generators.display.addDisplay(
        state.authState.currentState.hostAuthId,
        width,
        height,
        refreshRate,
        deviceName,
        serialNumber,
        true,
        guid,
      );
      ipcMessaging.send(msg);
    },
    shadowDisplayRemoveDisplay: (guid: string) => {
      console.log('[IPC-Fns] Removing virtual display', guid);
      const msg = generators.display.removeDisplay(state.authState.currentState.hostAuthId, guid);
      ipcMessaging.send(msg);
    },
    shadowDisplayRemoveAllDisplays: () => {
      console.log('[IPC-Fns] Removing all virtual displays');
      const msg = generators.display.removeAllDisplays(
        state.authState.currentState.hostAuthId,
        true,
        false,
        false,
      );
      ipcMessaging.send(msg);
    },
    shadowDisplayGetDisplays: () => {
      console.log('[IPC-Fns] Getting virtual displays');
      const msg = generators.display.getDisplays(state.authState.currentState.hostAuthId);
      ipcMessaging.send(msg);
    },
    shadowDisplayGetDriverStatus: () => {
      console.log('[IPC-Fns] Getting driver status');
      const msg = generators.display.getDriverStatus(state.authState.currentState.hostAuthId);
      ipcMessaging.send(msg);
    },
    shadowDisplayPingDriver: () => {
      console.log('[IPC-Fns] Pinging driver');
      const msg = generators.display.pingDriver(state.authState.currentState.hostAuthId);
      ipcMessaging.send(msg);
    },
    shadowDisplaySetRenderAdapter: (adapterLuid: { lowPart: number; highPart: number }) => {
      console.log('[IPC-Fns] Setting render adapter', adapterLuid);
      const msg = generators.display.setRenderAdapter(state.authState.currentState.hostAuthId, adapterLuid);
      ipcMessaging.send(msg);
    },
    shadowDisplayGetWatchdog: () => {
      console.log('[IPC-Fns] Getting watchdog status');
      const msg = generators.display.getWatchdog(state.authState.currentState.hostAuthId);
      ipcMessaging.send(msg);
    },
    shadowDisplayGetProtocolVersion: () => {
      console.log('[IPC-Fns] Getting protocol version');
      const msg = generators.display.getProtocolVersion(state.authState.currentState.hostAuthId);
      ipcMessaging.send(msg);
    },
    // Machine ID Attestation
    attestMachineId: async (clerkUserId: string, clerkToken: string) => {
      console.log('[IPC-Fns] Attesting machine ID for user:', clerkUserId);
      await runAttestationFlow(clerkUserId, clerkToken);
    },
    checkAttestationStatus: () => {
      const attestationState = state.machineIdAttestationState.currentState;
      return {
        status: attestationState.status,
        error: attestationState.error,
      };
    },
  };

  // Stream recovery after reload, put in timeout so functions resolve first
  setTimeout(() => {
    // Auto-apply AFK prevention if it was enabled previously
    try {
      const saved = localStorage.getItem('afkPreventionState');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed === 'object') {
          // Update store first so UI reflects persisted state
          state.afkPreventionState.updateState({
            ...state.afkPreventionState.currentState,
            ...parsed,
          });
          if (parsed.enabled === true) {
            setAfkPreventionSettings(parsed);
            setAfkPreventionEnabled(true);
            console.log('[IPC-Fns] Restored AFK prevention from localStorage');
          }
        }
      }
    } catch (err) {
      console.warn('[IPC-Fns] Failed to restore AFK prevention from localStorage', err);
    }

    const lastStreamTime = localStorage.getItem('lastStreamTime');
    const currentTime = new Date().getTime();
    const RELOAD_TIMEOUT = 5000;
    if (lastStreamTime) {
      const timeSinceLastStream = currentTime - parseInt(lastStreamTime, 10);
      console.warn('[Host] Time since last stream', timeSinceLastStream);
      if (timeSinceLastStream < RELOAD_TIMEOUT && state.hostState.currentState.status === 'idle') {
        console.warn('[Host] Reloading stream');
        localStorage.setItem('autoStream', 'true');
        setTimeout(() => {
          localStorage.removeItem('autoStream');
        }, 5000);
        if (state.hostState.currentState.edition === 'standard') {
          ipcFunctions.startStream();
        }
      }
      // localStorage.removeItem('lastStreamTime');
    }
  }, 1000);

  return ipcFunctions;
};

// export const destroyIpcInterface = () => {
//   if (loginProfileState.currentState.idToken) {
//     api.deactivateMachine(electronState.machineIdStore.currentState);
//   }
//   ipcFunctions?.stopStream();
//   // eslint-disable-next-line @typescript-eslint/no-explicit-any
//   ipcFunctions = undefined as any;
//   functionConnectionState.updateState({
//     connected: false,
//     initializing: false,
//     functionKey: '',
//     error: false,
//   });
//   disableRemoteControl();
//   destroyAllSharedStores();
//   destroyAllElectronStores();
// };

const updateInputAccess = (access: GlobalInputAccess, playerId?: string) => {
  if (!playerId) {
    console.debug('[IPC-Fns] Updating global input access', access);
    state.globalInputState.updateState({ ...access });
  } else {
    if (state.playerState.currentState.players[playerId]) {
      console.debug('[IPC-Fns] Updating player input access', access, playerId);
      const player = structuredClone(state.playerState.currentState.players[playerId]);
      const newState = { ...state.playerState.currentState };
      newState.players[playerId] = {
        ...player,
        access: { ...player.access, ...access },
      };
      state.playerState.updateState({ ...newState });
    }
  }
};

// add a browser event listener to listen for the 'beforeunload' event to track reloads and close events
window.addEventListener('beforeunload', (event) => {
  const streaming = state.hostState.currentState.status === 'streaming';
  if (streaming) {
    const currentTime = new Date().getTime();
    localStorage.setItem('lastStreamTime', currentTime.toString());
    // send to sentry so we can see logs of when this happens
    Sentry.captureMessage('Stream stopped due to reload or close');
  }
});
